import { Box, Button, Card, FilledInput, Typography, useMediaQuery, useTheme } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAtomValue } from 'jotai';
import { BusinessProfile } from '@kheops/graphql';
import ResumeNegociationImage from '../assets/images/resume_negociation.png';
import ResumeNegociationImageLandscape from '../assets/images/resume_negociation_landscape.png';
import { RoutePaths } from '../routes/AppRoutes';
import { useCreateContractRequestMutation } from '../mutations/__generated__/createContractRequest.generated';
import { currentContextAtom, userAtom } from '../state';
import { ContractPreview } from '../hooks/useContracts';
import { ContractsAsSupplierDocument } from '../queries/__generated__/contractsAsSupplier.generated';
import { CompanyByIdDocument } from '../queries/__generated__/companyById.generated';
import { ContractsAsBuyerDocument } from '../queries/__generated__/contractsAsBuyer.generated';
import Sheet from '../common/components/Sheet';
import ContactsByDepartment from './buyer/contacts/ContactsByDepartment';
import ResumeNegotiationButton from './ResumeNegotiationButton';

export interface CompanyContactsProps {
  companyId: string;
  contract?: ContractPreview;
}

export default function CompanyContacts({ companyId, contract }: CompanyContactsProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
  const context = useAtomValue(currentContextAtom);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { id: userId } = useAtomValue(userAtom);
  const [createContractRequest, { data: createContractRequestData, loading: loadingContractCreation }] = useCreateContractRequestMutation({
    refetchQueries: [CompanyByIdDocument, ContractsAsSupplierDocument, ContractsAsBuyerDocument],
    awaitRefetchQueries: true,
  });
  const [message, setMessage] = useState('');

  const handleContractRequestSubmit = useCallback((): void => {
    createContractRequest({
      variables: {
        userId,
        buyingCompanyId: companyId,
        supplyingCompanyId: context.companyId!,
        fromBusinessProfile: context.realm as unknown as BusinessProfile,
        message,
      },
    });
  }, [context, message]);

  useEffect(() => {
    if (createContractRequestData?.createContractRequest?.channelUrl) {
      navigate(`${RoutePaths.CHAT_LIST}?channelUrl=${createContractRequestData.createContractRequest.channelUrl}`);
    }
  }, [createContractRequestData]);

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: 'surfaceContainer.main',
        p: 2,
      }}
    >
      {!contract?.status && (
        <>
          <Box>
            <Typography variant="titleLarge" sx={{ mb: 0.5 }}>
              {t('contracts:contact_this_store')}
            </Typography>
            <Typography
              variant="bodySmall"
              sx={{
                color: 'onSurfaceVariant.main',
              }}
            >
              {t('contracts:contact_this_store_description')}
            </Typography>
          </Box>
          <FilledInput
            fullWidth
            inputProps={{
              style: {
                minHeight: 120,
              },
            }}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('contracts:type_your_message')}
            multiline
          />
          <Box sx={{ display: 'flex', flexDirection: { sm: 'row', xs: 'column-reverse' }, gap: 1 }}>
            {contract && (
              <Button
                onClick={() => setDrawerOpen(true)}
                variant="outlined"
                startIcon={<PermIdentityIcon />}
                sx={{
                  alignSelf: {
                    sm: 'center',
                  },
                }}
              >
                {t('common:contacts')}
              </Button>
            )}
            <Button
              onClick={handleContractRequestSubmit}
              loading={loadingContractCreation}
              variant="contained"
              disabled={!message}
              startIcon={<SendIcon />}
              sx={{
                alignSelf: {
                  sm: 'center',
                },
              }}
            >
              {t('common:send_the_message')}
            </Button>
          </Box>
        </>
      )}
      {contract && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            gap: 3,
            my: 0,
          }}
        >
          <Box
            component="img"
            src={isSmallerThanMd ? ResumeNegociationImageLandscape : ResumeNegociationImage}
            sx={{
              borderRadius: 4,
              width: {
                md: 172,
              },
              height: {
                xs: 116.667,
                sm: 194.667,
                md: 172,
              },
            }}
          />
          <Box>
            <Typography variant="titleLarge" sx={{ mb: 0.5, pt: { lg: 1 } }}>
              {t('contracts:negotiations_in_progress')}
            </Typography>
            <Typography
              variant="bodySmall"
              sx={{
                color: 'onSurfaceVariant.main',
                mb: 2
              }}
            >
              {t('contracts:resume_negociation_description')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: {
                  xs: 'column-reverse',
                  sm: 'row',
                },
                gap: 1,
              }}
            >
              <Button
                onClick={() => setDrawerOpen(true)}
                variant="outlined"
                startIcon={<PermIdentityIcon />}
                sx={{
                  alignSelf: {
                    sm: 'center',
                  },
                }}
              >
                {t('common:contacts')}
              </Button>
              <ResumeNegotiationButton contract={contract} />
            </Box>
          </Box>
        </Box>
      )}
      <Sheet
        open={drawerOpen}
        width={320}
        onClose={() => setDrawerOpen(false)}
        title={t('common:contacts')}
        titleIcon={<PermIdentityIcon />}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ContactsByDepartment companyId={companyId} displayContactInfo />
        </Box>
      </Sheet>
    </Card>
  );
}
